/**
 * Activity API Client
 * Interfaces with orchestration-service activity endpoints
 */

import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

// API base URL - relative path works with vite proxy (dev) and cloudflare tunnel (prod)
const API_BASE = '/api/orchestration';

/**
 * Activity event from SSE stream
 */
export interface ActivityEvent {
  id: string;
  timestamp: string;
  category: 'workflow' | 'sprite' | 'build' | 'test' | 'deploy' | 'review' | 'system' | 'training';
  severity: 'info' | 'warning' | 'error' | 'critical' | 'success';
  title: string;
  description?: string;
  metadata?: Record<string, unknown>;
  workflowId?: string;
  executionId?: string;
  projectId?: string;
}

/**
 * Dashboard summary data
 */
export interface ActivityDashboardData {
  summary: {
    activeWorkflows: number;
    totalExecutions: number;
    totalCost: number;
    errorRate: number;
  };
  recentActivity: ActivityEvent[];
  topWorkflows: Array<{
    workflowId: string;
    name: string;
    executions: number;
    successRate: number;
    avgDuration: number;
  }>;
  costBreakdown: {
    compute: number;
    storage: number;
    network: number;
    other: number;
  };
  timestamp: string;
}

/**
 * Timeline query parameters
 */
export interface TimelineParams {
  startTime?: string;
  endTime?: string;
  categories?: string[];
  severities?: string[];
  projectId?: string;
  workflowId?: string;
  limit?: number;
}

/**
 * Timeline data
 */
export interface TimelineData {
  events: ActivityEvent[];
  count: number;
  hasMore: boolean;
}

/**
 * Workflow data
 */
export interface WorkflowData {
  workflowId: string;
  name: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  startTime: string;
  endTime?: string;
  executions: number;
  successRate: number;
  avgDuration: number;
  lastExecution?: string;
  projectId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Cost query parameters
 */
export interface CostParams {
  startTime?: string;
  endTime?: string;
  projectId?: string;
  granularity?: 'hour' | 'day' | 'week' | 'month';
}

/**
 * Cost data
 */
export interface CostData {
  total: number;
  breakdown: {
    compute: number;
    storage: number;
    network: number;
    other: number;
  };
  timeline: Array<{
    timestamp: string;
    cost: number;
    breakdown: {
      compute: number;
      storage: number;
      network: number;
      other: number;
    };
  }>;
  period: {
    start: string;
    end: string;
  };
}

/**
 * Activity API client
 */
export const activityApi = {
  /**
   * Get dashboard summary data
   */
  async getDashboard(): Promise<ActivityDashboardData> {
    const response = await fetch(`${API_BASE}/activity/dashboard`);
    if (!response.ok) {
      throw new Error(`Failed to fetch dashboard: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get timeline events
   */
  async getTimeline(params: TimelineParams = {}): Promise<TimelineData> {
    const queryParams = new URLSearchParams();
    if (params.startTime) queryParams.set('startTime', params.startTime);
    if (params.endTime) queryParams.set('endTime', params.endTime);
    if (params.categories) queryParams.set('categories', params.categories.join(','));
    if (params.severities) queryParams.set('severities', params.severities.join(','));
    if (params.projectId) queryParams.set('projectId', params.projectId);
    if (params.workflowId) queryParams.set('workflowId', params.workflowId);
    if (params.limit) queryParams.set('limit', String(params.limit));

    const url = `${API_BASE}/activity/timeline${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch timeline: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get workflows
   */
  async getWorkflows(): Promise<WorkflowData[]> {
    const response = await fetch(`${API_BASE}/activity/workflows`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workflows: ${response.statusText}`);
    }
    const data = await response.json();
    return data.workflows || [];
  },

  /**
   * Get cost data
   */
  async getCosts(params: CostParams = {}): Promise<CostData> {
    const queryParams = new URLSearchParams();
    if (params.startTime) queryParams.set('startTime', params.startTime);
    if (params.endTime) queryParams.set('endTime', params.endTime);
    if (params.projectId) queryParams.set('projectId', params.projectId);
    if (params.granularity) queryParams.set('granularity', params.granularity);

    const url = `${API_BASE}/activity/costs${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch costs: ${response.statusText}`);
    }
    return response.json();
  },
};

/**
 * SSE hook options
 */
export interface ActivityStreamOptions {
  categories?: string[];
  severities?: string[];
  enabled?: boolean;
}

/**
 * SSE hook return type
 */
export interface ActivityStreamResult {
  events: ActivityEvent[];
  isConnected: boolean;
  error: Error | null;
}

/** Normalize an incoming SSE event to match the ActivityEvent interface */
function normalizeEvent(raw: Record<string, unknown>): ActivityEvent {
  // Map severity: backend may send 'debug' or 'critical' — normalize to frontend union
  const severityMap: Record<string, ActivityEvent['severity']> = {
    info: 'info', warning: 'warning', error: 'error', success: 'success',
    debug: 'info', critical: 'critical',
  };
  // Map category: backend now sends ActivityBus categories, but handle legacy too
  const categoryMap: Record<string, ActivityEvent['category']> = {
    workflow: 'workflow', sprite: 'sprite', build: 'build', test: 'test',
    deploy: 'deploy', review: 'review', system: 'system', training: 'training',
    // Legacy mappings
    execution: 'sprite', cost: 'system', agent: 'sprite', issue: 'workflow', sprint: 'workflow',
  };
  return {
    ...raw,
    id: String(raw.id || crypto.randomUUID()),
    timestamp: String(raw.timestamp || new Date().toISOString()),
    title: String(raw.title || 'Event'),
    description: raw.description ? String(raw.description) : undefined,
    category: categoryMap[raw.category as string] || 'system',
    severity: severityMap[raw.severity as string] || 'info',
    metadata: (raw.metadata as Record<string, unknown>) || undefined,
  } as ActivityEvent;
}

/**
 * Hook for streaming activity events via SSE
 * Follows the pattern from LogsPage EventSource usage
 */
export function useActivityStream(options: ActivityStreamOptions = {}): ActivityStreamResult {
  const { categories, severities, enabled = true } = options;

  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const connect = () => {
      try {
        // Build URL with query parameters
        const params = new URLSearchParams();
        if (categories && categories.length > 0) {
          params.set('categories', categories.join(','));
        }
        if (severities && severities.length > 0) {
          params.set('severities', severities.join(','));
        }

        const url = `${API_BASE}/activity/stream${params.toString() ? `?${params}` : ''}`;
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          setIsConnected(true);
          setError(null);
        };

        // Handle named 'backfill' event — sends an array of historical events
        eventSource.addEventListener('backfill', (event: MessageEvent) => {
          try {
            const backfillEvents: ActivityEvent[] = JSON.parse(event.data);
            if (Array.isArray(backfillEvents) && backfillEvents.length > 0) {
              setEvents((prev) => {
                const merged = [...prev, ...backfillEvents.map(normalizeEvent)];
                return merged.slice(-500);
              });
            }
          } catch (err) {
            console.error('Failed to parse backfill events:', err);
          }
        });

        // Handle unnamed live events (default message type)
        eventSource.onmessage = (event) => {
          try {
            const activityEvent: ActivityEvent = normalizeEvent(JSON.parse(event.data));
            setEvents((prev) => {
              const newEvents = [...prev, activityEvent];
              return newEvents.slice(-500);
            });
          } catch (err) {
            console.error('Failed to parse activity event:', err);
          }
        };

        eventSource.onerror = (err) => {
          console.error('EventSource error:', err);
          setIsConnected(false);
          setError(new Error('Connection lost'));
          eventSource.close();

          // Reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Reconnecting to activity stream...');
            connect();
          }, 5000);
        };
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to connect'));
        setIsConnected(false);
      }
    };

    connect();

    // Cleanup on unmount or when options change
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, categories?.join(','), severities?.join(',')]);

  return { events, isConnected, error };
}

/**
 * React Query hook for dashboard data
 */
export function useActivityDashboard() {
  return useQuery({
    queryKey: ['activity', 'dashboard'],
    queryFn: () => activityApi.getDashboard(),
    refetchInterval: 15000, // Poll every 15s
    staleTime: 10000, // Consider stale after 10s
  });
}

/**
 * React Query hook for timeline data
 */
export function useActivityTimeline(params: TimelineParams = {}) {
  return useQuery({
    queryKey: ['activity', 'timeline', params],
    queryFn: () => activityApi.getTimeline(params),
    refetchInterval: 15000, // Poll every 15s
    staleTime: 10000,
  });
}

/**
 * React Query hook for workflows
 */
export function useWorkflows() {
  return useQuery({
    queryKey: ['activity', 'workflows'],
    queryFn: () => activityApi.getWorkflows(),
    refetchInterval: 15000, // Poll every 15s
    staleTime: 10000,
  });
}

/**
 * React Query hook for cost data
 */
export function useActivityCosts(params: CostParams = {}) {
  return useQuery({
    queryKey: ['activity', 'costs', params],
    queryFn: () => activityApi.getCosts(params),
    refetchInterval: 30000, // Poll every 30s
    staleTime: 20000, // Consider stale after 20s
  });
}

export default activityApi;
